// Разбор ответа модели: блоки и выделения. SMY-115
//
// Модель отвечает markdown'ом, даже когда её просят иначе: `**жирный**` и
// строки-пункты она ставит по привычке (docs/model-behaviour.md, замер
// 05.09.2026). До этой правки текст уходил в ленту как есть, и человек читал
// «* **Состав**. В энергетических напитках…» — то есть служебные знаки вместо
// структуры, ради которой они и поставлены.
//
// ПОЧЕМУ РИСУЕМ, А НЕ ВЫЧИЩАЕМ. В ядре есть `plainText`, и он снимает те же
// маркеры — но он для ЗАГОЛОВКА в списке, где структуры не нужно вовсе.
// Ответу она нужна: три пункта про состав, количество и эффект — это три
// пункта, а не абзац с точками. Снять маркеры значило бы превратить
// разобранный ответ в стену текста.
//
// Разбор нарочно узкий: заголовки, абзацы, маркированные и нумерованные
// списки, разделительная черта, жирный и моноширинный внутри строки. Ссылок и
// таблиц здесь нет — их модель в ответе чата не ставит, а полноценный парсер
// markdown это зависимость размером с сам кабинет ради нескольких правил.
//
// ЗАГОЛОВКИ ЗДЕСЬ ТОЖЕ СЧИТАЛИСЬ НЕВОЗМОЖНЫМИ — в этом месте было написано,
// что модель в ответе чата их не ставит. 07.09.2026 пришёл план тренировок,
// разбитый на «## Общие рекомендации» и «### Разминка»: длинный ответ модель
// делит на разделы, и решётки уехали в ленту текстом — ровно та беда, из-за
// которой этот файл и заведён, только другим знаком. Замечание снято, правило
// осталось прежним: знак в начале строки означает структуру, и показывать
// надо структуру.
//
// Отдельно от компонента (`Answer.tsx`), потому что это чистые правила и их
// надо проверять тестом: `node --experimental-strip-types` умеет `.ts` и не
// умеет `.tsx`. Та же граница, что у `icons.ts` и `Icon.tsx`.
//
// Имя файла не `answer.ts` намеренно: на macOS файловая система нечувствительна
// к регистру, и `answer.ts` рядом с `Answer.tsx` дают одно и то же имя на
// выходе — сборка типов падает с «overwritten by multiple input files».

/** Строка списка: «* пункт», «- пункт», «• пункт». */
const BULLET = /^\s*[-*•]\s+/
/** Строка нумерованного списка: «1. пункт», «2) пункт». */
const NUMBERED = /^\s*\d+[.)]\s+/
/**
 * Заголовок раздела: «## Советы».
 *
 * ПРОБЕЛ ПОСЛЕ РЕШЁТОК ОБЯЗАТЕЛЕН, и это не формальность: «#5» и «#хэштег» —
 * обычный текст, и заголовок из них означал бы потерю смысла там, где никакой
 * разметки не было. То же правило, что у одиночной звёздочки ниже.
 *
 * Уровень запоминается: в одном ответе модель ставит и «##», и «###», и
 * разница между ними — это и есть структура, ради которой знаки поставлены.
 * Закрывающие решётки («## Советы ##») отрезаются только вместе с пробелом
 * перед ними — иначе «## Язык C#» потерял бы решётку, которая часть слова.
 */
const HEADING = /^\s*(#{1,6})\s+(.+?)(?:\s+#+)?$/
/**
 * Разделительная черта: «---», «***», «___» в одиночестве на строке.
 *
 * Своего содержимого у неё нет, но и молчать о ней нельзя: непонятая строка
 * уходит в ленту абзацем из трёх дефисов. Проверяется до пунктов — «---» на
 * пункт не похоже, но порядок здесь важнее экономии одной проверки.
 */
const RULE = /^\s*(?:-{3,}|\*{3,}|_{3,})\s*$/

export type Block =
  | { kind: 'text'; lines: string[] }
  | { kind: 'bullets'; items: string[] }
  | { kind: 'numbers'; items: string[] }
  | { kind: 'heading'; level: number; text: string }
  | { kind: 'rule' }

/**
 * Разбор текста на блоки.
 *
 * Пустая строка заканчивает абзац, строка-пункт начинает список. Порядок
 * важен: пункт внутри абзаца — это всё-таки пункт, и склеивать его с
 * предыдущей строкой нельзя.
 */
export function blocksOf(text: string): Block[] {
  const blocks: Block[] = []
  let broken = false

  for (const raw of text.split('\n')) {
    const line = raw.trimEnd()
    if (line.trim() === '') {
      // Пустая строка закрывает текущий абзац, но своего блока не создаёт.
      broken = true
      continue
    }

    const last = blocks.at(-1)
    if (RULE.test(line)) {
      blocks.push({ kind: 'rule' })
      broken = false
      continue
    }
    const heading = HEADING.exec(line)
    if (heading?.[1] != null && heading[2] != null) {
      blocks.push({ kind: 'heading', level: heading[1].length, text: heading[2] })
      broken = false
      continue
    }
    if (BULLET.test(line)) {
      const item = line.replace(BULLET, '')
      if (last?.kind === 'bullets') last.items.push(item)
      else blocks.push({ kind: 'bullets', items: [item] })
      broken = false
      continue
    }
    if (NUMBERED.test(line)) {
      const item = line.replace(NUMBERED, '')
      if (last?.kind === 'numbers') last.items.push(item)
      else blocks.push({ kind: 'numbers', items: [item] })
      broken = false
      continue
    }
    if (!broken && last?.kind === 'text') last.lines.push(line)
    else blocks.push({ kind: 'text', lines: [line] })
    broken = false
  }
  return blocks
}

/** Кусок строки: обычный текст, выделенный или моноширинный. */
export type Token = { kind: 'text' | 'bold' | 'code'; text: string }

/**
 * Парные маркеры внутри строки.
 *
 * Одиночная звёздочка не трогается намеренно: в тексте она бывает умножением
 * и сноской, и «почистить» её значит испортить смысл. То же правило, что у
 * `plainText` в ядре.
 */
const INLINE = /(\*\*[^*]+\*\*|__[^_]+__|`[^`]+`)/g

export function inline(text: string): Token[] {
  return text
    .split(INLINE)
    .filter((part) => part !== '')
    .map((part): Token => {
      if (part.startsWith('**') && part.endsWith('**')) return { kind: 'bold', text: part.slice(2, -2) }
      if (part.startsWith('__') && part.endsWith('__')) return { kind: 'bold', text: part.slice(2, -2) }
      if (part.startsWith('`') && part.endsWith('`')) return { kind: 'code', text: part.slice(1, -1) }
      return { kind: 'text', text: part }
    })
}

/** Строка без парных маркеров. Разбор тот же, что у отрисовки выделений. */
const flat = (line: string): string =>
  inline(line)
    .map((token) => token.text)
    .join('')

/**
 * Ответ без разметки — то, что уходит в буфер обмена. SMY-115
 *
 * До 07.09.2026 кнопка копировала исходник целиком, со звёздочками: считалось,
 * что ответ вставят туда, где разметка снова станет разметкой. Вставляют его в
 * письмо, в заметку и в переписку, и там «**Разминка**» остаётся звёздочками —
 * то есть человек уносит с собой ровно то, от чего этот файл избавил экран.
 *
 * СВОИХ ПРАВИЛ ЗДЕСЬ НЕТ, и это главное. Текст собирается из тех же блоков,
 * которыми ответ рисуется, — скопировать не то, что видно, попросту нечем.
 * Второй набор правил рядом с первым разошёлся бы с ним молча: как раз так и
 * разошлись когда-то `plainText` в ядре и заголовок результата в истории.
 *
 * Структура остаётся, потому что она осталась и на экране: пункт уходит
 * пунктом, раздел — строкой, абзац — абзацем. «Без разметки» человек просит,
 * чтобы не тащить служебные знаки, а не чтобы получить стену текста.
 *
 * Точка у пункта — «•», а не «-» и не «*»: дефис и звёздочка в начале строки
 * это снова разметка, которую следующий читатель (и следующая модель) разберёт
 * обратно. «•» ничем не притворяется и выглядит так же, как на экране.
 *
 * Нумерация своя, с единицы: на экране её рисует `<ol>`, и ответ, у которого
 * модель начала список с «3)», в обоих местах читается одинаково.
 *
 * Черта своего текста не имеет и исчезает: между блоками и так пустая строка,
 * а «---» в буфере — это опять разметка.
 */
export function plainAnswer(text: string): string {
  const blocks = blocksOf(text).flatMap((block): string[] => {
    if (block.kind === 'text') return [block.lines.map(flat).join(' ')]
    if (block.kind === 'heading') return [flat(block.text)]
    if (block.kind === 'bullets') return [block.items.map((item) => `• ${flat(item)}`).join('\n')]
    if (block.kind === 'numbers') return [block.items.map((item, i) => `${i + 1}. ${flat(item)}`).join('\n')]
    return []
  })
  // Пустая строка между блоками — то же расстояние, что и зазор между ними на
  // экране: другого способа показать границу абзаца в тексте нет.
  return blocks.join('\n\n')
}
